/* ==========================================================================
   Collection v2 : opération Smoon Days (2 produits achetés, le 3e offert).

   1. Dates refaites dans le navigateur (heure de Paris) : la ligne et le
      compteur apparaissent au début et disparaissent à la fin sans recharger,
      y compris sur une page servie depuis le cache de Shopify.
   2. Fenêtre « Comment ça marche », ouverte par la ligne d'offre.
   3. Compteur : produits du panier qui sont dans la collection de la remise
      (liste chargée une fois par session, section cv2-offre-ids). Visible
      quand la ligne d'offre sort de l'écran, ou dès le premier produit
      concerné. Suit chaque changement du panier : le thème n'a pas de bus
      d'événements, on observe les appels aux routes du panier (même méthode
      que l'encart cadeau des fiches), plus les événements cart:refresh.
   4. Un produit hors offre ajouté : le compteur le dit quelques secondes.

   La page affiche l'offre, elle ne l'applique pas : la remise est la remise
   automatique de Shopify.

   Fiches produit (main-product, doris, 30/09/2026) : la même ligne sous le
   prix, sans pastille flottante ; le compteur est une bande en haut de la
   barre d'achat collante (variante « achat »), montrée pendant toute
   l'opération et écrite sur une ligne. Le thème réécrit cette barre à chaque
   changement de variante : elle est repeinte aussitôt.
   ========================================================================== */
(() => {
  'use strict';

  if (window.cv2OffrePret) return;
  window.cv2OffrePret = true;

  const q = (sel, root = document) => root.querySelector(sel);
  const qa = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const noeud = document.getElementById('cv2-offre-config');
  if (!noeud) return;
  let cfg = {};
  try {
    cfg = JSON.parse(noeud.textContent);
  } catch (e) {
    return;
  }
  const T = cfg.textes || {};

  /* 1. Dates ---------------------------------------------------------------- */

  /* « 2026-09-30 00:00 », heure de Paris → horodatage (repris de move.js). */
  function parisTime(text) {
    const m = /^\s*(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T]+(\d{1,2})(?:[:h](\d{2}))?)?\s*$/.exec(text || '');
    if (!m) return null;
    const guess = Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0));
    try {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Paris',
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).formatToParts(new Date(guess));
      const get = (type) => +parts.find((p) => p.type === type).value;
      const shown = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'));
      return guess - (shown - guess);
    } catch (e) {
      return guess - 2 * 3600 * 1000; /* repli : heure d'été de Paris */
    }
  }

  const debut = parisTime(cfg.debut);
  const fin = parisTime(cfg.fin);

  /* Aperçu avant le début : ?smoondays=apercu dans l'adresse montre tout sans
     attendre l'heure, pour qui a le lien. Sans effet après la fin : la page
     ne rend alors plus rien de l'opération. */
  let apercu = false;
  try {
    apercu = new URLSearchParams(location.search).get('smoondays') === 'apercu';
  } catch (e) {
    /* rien */
  }

  const enCours = () => {
    if (apercu) return true;
    const t = Date.now();
    return !(debut !== null && t < debut) && !(fin !== null && t >= fin);
  };

  /* État ---------------------------------------------------------------------- */

  let n = 0; /* produits concernés dans le panier */
  let horsJusqua = 0; /* message « hors offre » affiché jusqu'à */
  let ligneVue = false;
  let actifAvant = null;
  const flottant = !!q('.cv2-offre-compteur--flottant');

  const racine = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* 3. Produits concernés : une requête par session ------------------------- */

  let ids = null;
  function chargerIds() {
    if (ids) return ids;
    const cle = 'cv2-offre-ids:' + cfg.ids;
    try {
      const m = JSON.parse(sessionStorage.getItem(cle) || 'null');
      if (m && Array.isArray(m.ids) && Date.now() - m.t < 30 * 60 * 1000) {
        ids = Promise.resolve(new Set(m.ids));
        return ids;
      }
    } catch (e) {
      /* stockage indisponible : on charge */
    }
    ids = fetch(cfg.ids, { credentials: 'same-origin' })
      .then((r) => {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const s = q('[data-cv2-offre-ids]', doc);
        const liste = s ? JSON.parse(s.textContent) : null;
        if (!Array.isArray(liste)) throw new Error('liste absente');
        try {
          sessionStorage.setItem(cle, JSON.stringify({ t: Date.now(), ids: liste }));
        } catch (e) {
          /* rien */
        }
        return new Set(liste);
      })
      .catch(() => {
        /* nouvel essai au prochain changement du panier */
        ids = null;
        return null;
      });
    return ids;
  }

  let enVol = null;
  let encore = false;
  function rafraichir() {
    if (!enCours()) {
      peindre();
      return null;
    }
    if (enVol) {
      encore = true;
      return enVol;
    }
    enVol = Promise.all([
      chargerIds(),
      fetch(racine + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then((r) =>
        r.ok ? r.json() : null
      ),
    ])
      .then(([liste, panier]) => {
        if (!liste || !panier || !Array.isArray(panier.items)) return;
        n = panier.items.reduce((s, it) => s + (liste.has(it.product_id) ? it.quantity : 0), 0);
      })
      .catch(() => {
        /* panier illisible : le compteur garde son dernier état */
      })
      .then(() => {
        enVol = null;
        peindre();
        if (encore) {
          encore = false;
          rafraichir();
        }
      });
    return enVol;
  }

  /* 4. Produit ajouté hors de la collection de la remise. */
  function verifierAjout(data) {
    if (!enCours() || !data) return;
    const items = Array.isArray(data.items) ? data.items : data.product_id ? [data] : [];
    if (!items.length) return;
    Promise.resolve(chargerIds()).then((liste) => {
      if (!liste || !items.some((it) => !liste.has(it.product_id))) return;
      horsJusqua = Date.now() + 4500;
      peindre();
      setTimeout(peindre, 4600);
    });
  }

  /* Affichage ------------------------------------------------------------------ */

  const avec = (texte, v) => String(texte || '').split('[n]').join(String(v));

  /* Bande de la barre d'achat : titre et détail sur une ligne. « Pour 2
     achetés » devient « pour 2 achetés » ; un mot tout en capitales reste. */
  const minuscule = (t) => {
    const s = String(t || '');
    return /^\p{Lu}\p{Ll}/u.test(s) ? s.charAt(0).toLocaleLowerCase('fr') + s.slice(1) : s;
  };

  function etat() {
    const offerts = Math.floor(n / 3);
    const reste = n % 3;
    const pleins = n > 0 && reste === 0 ? 3 : reste;
    /* lie : sur une ligne, le détail suit le titre sans deux-points. */
    if (Date.now() < horsJusqua) return { titre: T.hors, texte: T.horsTexte, pleins, panier: offerts > 0 };
    if (n === 0) return { titre: T.vide, texte: T.videTexte, pleins: 0, panier: false, lie: true };
    if (offerts === 0) return { titre: reste === 1 ? T.un : T.deux, texte: T.avant, pleins, panier: false, lie: true };
    let texte;
    if (reste === 0) texte = offerts === 1 ? T.trio : T.complet;
    else texte = avec(reste === 1 ? T.encore2 : T.encore1, offerts + 1);
    return { titre: offerts === 1 ? T.offert : avec(T.offerts, offerts), texte, pleins, panier: true };
  }

  function peindre() {
    const actif = enCours();
    if (actif !== actifAvant) {
      actifAvant = actif;
      qa('[data-cv2-offre]').forEach((el) => {
        el.hidden = !actif;
      });
      /* Page en cache servie après la fin : les mentions des cartes partent aussi. */
      qa('.cv2-badge--hors').forEach((b) => {
        b.hidden = !actif;
      });
      if (actif) rafraichir();
    }

    const montrer = actif && (n > 0 || !ligneVue || Date.now() < horsJusqua);
    const e = etat();
    qa('[data-cv2-offre-compteur]').forEach((c) => {
      const vu = c.hasAttribute('data-cv2-offre-toujours') ? actif : montrer;
      c.hidden = !vu;
      if (!vu) return;
      const t = q('[data-cv2-offre-titre]', c);
      const x = q('[data-cv2-offre-texte]', c);
      const p = q('[data-cv2-offre-panier]', c);
      const texte = c.classList.contains('cv2-offre-compteur--achat')
        ? (e.lie ? ' ' : '\u00a0: ') + minuscule(e.texte)
        : e.texte || '';
      if (t && t.textContent !== e.titre) t.textContent = e.titre || '';
      if (x && x.textContent !== texte) x.textContent = texte;
      if (p) p.hidden = !e.panier;
      qa('.cv2-offre-compteur__cases i', c).forEach((i, k) => i.classList.toggle('is-plein', k < e.pleins));
    });
    /* Réserve sous la page pour la pastille flottante, s'il y en a une. */
    document.documentElement.classList.toggle('cv2-offre-bas', montrer && flottant);
  }

  /* 2. Fenêtre « Comment ça marche » ------------------------------------------- */

  const fige = (oui) => document.documentElement.classList.toggle('cv2-fige', oui);
  const dlg = q('[data-cv2-offre-feuille]');
  if (dlg && typeof dlg.showModal === 'function') {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-cv2-offre-ligne]');
      if (!btn || dlg.open) return;
      e.preventDefault();
      dlg.showModal();
      fige(true);
    });
    dlg.addEventListener('click', (e) => {
      if (e.target === dlg || e.target.closest('[data-cv2-fermer]')) dlg.close();
    });
    dlg.addEventListener('close', () => fige(false));
  }

  /* Bouton « Panier » : ouvre le tiroir UpCart, sinon mène à /cart. */
  document.addEventListener('click', (e) => {
    const a = e.target.closest('[data-cv2-offre-panier]');
    if (!a || typeof window.upcartOpenCart !== 'function') return;
    e.preventDefault();
    try {
      window.upcartOpenCart();
    } catch (err) {
      location.href = a.href;
    }
  });

  /* Compteur flottant : dans <body>, pour qu'aucun ancêtre transformé ne le
     décroche du bas de l'écran. */
  qa('.cv2-offre-compteur--flottant').forEach((c) => document.body.appendChild(c));

  /* Fiche produit : au changement de variante, le thème remplace le contenu
     de la barre d'achat par celui du serveur, où la bande est vide et
     masquée. On la repeint dès le remplacement. */
  if ('MutationObserver' in window) {
    qa('sticky-atc').forEach((barre) => new MutationObserver(() => peindre()).observe(barre, { childList: true }));
  }

  /* Ligne d'offre à l'écran : le compteur attend (pas deux messages à la fois). */
  const ligne = q('[data-cv2-offre-ligne]');
  if (ligne && 'IntersectionObserver' in window) {
    ligneVue = true;
    new IntersectionObserver((entrees) => {
      ligneVue = entrees[0].isIntersecting;
      peindre();
    }).observe(ligne);
  }

  /* Changements du panier ------------------------------------------------------ */

  const ROUTES = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  const AJOUT = /\/cart\/add(\.js)?(\?|$)/;

  if (window.fetch && !window.__cv2OffreFetch) {
    window.__cv2OffreFetch = true;
    const natif = window.fetch;
    window.fetch = function (input) {
      const url = typeof input === 'string' ? input : (input && input.url) || String(input || '');
      /* `this || window` : appelé nu, `this` vaut undefined en mode strict. */
      const res = natif.apply(this || window, arguments);
      if (ROUTES.test(url)) {
        res.then(
          (r) => {
            if (r && r.ok && AJOUT.test(url)) {
              try {
                r.clone().json().then(verifierAjout, () => {});
              } catch (e) {
                /* réponse déjà lue */
              }
            }
            setTimeout(rafraichir, 0);
          },
          () => {}
        );
      }
      return res;
    };
  }

  if (window.XMLHttpRequest && !window.__cv2OffreXhr) {
    window.__cv2OffreXhr = true;
    const ouvrir = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (methode, url) {
      try {
        if (ROUTES.test(String(url))) this.addEventListener('load', () => setTimeout(rafraichir, 0));
      } catch (e) {
        /* rien */
      }
      return ouvrir.apply(this, arguments);
    };
  }

  ['cart:refresh', 'quantity:update'].forEach((nom) => {
    document.addEventListener(nom, () => rafraichir());
  });

  /* Tiroir UpCart (quantités, suppressions, ventes additionnelles) : ses
     abonnements, disponibles une fois son script chargé (asynchrone). */
  let upcartBranche = false;
  function brancherUpcart() {
    if (upcartBranche || typeof window.upcartSubscribeCartUpdated !== 'function') return;
    upcartBranche = true;
    try {
      window.upcartSubscribeCartUpdated(() => rafraichir());
      if (typeof window.upcartSubscribeItemRemoved === 'function') window.upcartSubscribeItemRemoved(() => rafraichir());
    } catch (e) {
      /* l'observation des routes du panier suffit */
    }
  }
  brancherUpcart();
  window.addEventListener('load', brancherUpcart);
  [3000, 8000].forEach((ms) => setTimeout(brancherUpcart, ms));
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) rafraichir();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') rafraichir();
  });

  /* Début et fin pendant la visite. */
  setInterval(peindre, 30000);

  peindre();
})();
